/**
 * Text-based input validation for numeric fields
 * Keeps type="text" for backend compatibility while ensuring data quality
 *
 * Backend stores values as JSON with numeric types, but frontend uses text inputs
 * to maintain compatibility with existing JSON serialization logic.
 */

/**
 * Validate and sanitize numeric text input
 * @param {string} value - Raw input value
 * @param {Object} options - Validation options
 * @returns {Object} { isValid: boolean, sanitized: string|null, error: string }
 */
export const validateNumericText = (value, options = {}) => {
  const {
    min = 0,
    max = 999,
    allowEmpty = false,
    allowNull = false,
    fieldName = "Value",
  } = options;

  // Handle empty/null
  if (value === "" || value === null || value === undefined) {
    if (allowEmpty || allowNull) {
      return { isValid: true, sanitized: null, error: "" };
    }
    return { isValid: false, sanitized: null, error: `${fieldName} is required` };
  }

  // Remove whitespace
  const trimmed = String(value).trim();

  // Check if numeric (whole numbers only)
  const numericPattern = /^\d+$/;
  if (!numericPattern.test(trimmed)) {
    return {
      isValid: false,
      sanitized: null,
      error: `${fieldName} must contain only numbers`,
    };
  }

  // Parse as number
  const parsed = Number(trimmed);

  // Check for NaN (shouldn't happen after pattern check, but safety first)
  if (isNaN(parsed)) {
    return {
      isValid: false,
      sanitized: null,
      error: `${fieldName} is not a valid number`,
    };
  }

  // Check for infinity
  if (!isFinite(parsed)) {
    return {
      isValid: false,
      sanitized: null,
      error: `${fieldName} is too large`,
    };
  }

  // Validate range
  if (parsed < min) {
    return {
      isValid: false,
      sanitized: null,
      error: `${fieldName} must be at least ${min}`,
    };
  }

  if (parsed > max) {
    return {
      isValid: false,
      sanitized: null,
      error: `${fieldName} cannot exceed ${max}`,
    };
  }

  // Return the number (will be serialized as number in JSON)
  return {
    isValid: true,
    sanitized: parsed,
    error: "",
  };
};

/**
 * Validate year range for leave rules
 * @param {string|number} fromYear
 * @param {string|number|null} toYear
 * @returns {Object}
 */
export const validateYearRange = (fromYear, toYear) => {
  // Parse values
  const from = fromYear === "" || fromYear === null ? null : Number(fromYear);
  const to = toYear === "" || toYear === null ? null : Number(toYear);

  // If toYear is null (unlimited), range is valid
  if (to === null) {
    return { isValid: true, error: "" };
  }

  // Both must be valid numbers for comparison
  if (from === null || isNaN(from) || isNaN(to)) {
    return { isValid: true, error: "" }; // Let individual validation handle this
  }

  // Check range
  if (from >= to) {
    return {
      isValid: false,
      error: "From Year must be less than To Year",
    };
  }

  return { isValid: true, error: "" };
};

/**
 * Restrict keyboard input to numeric characters only
 * Allows: numbers, backspace, delete, tab, escape, enter, arrows, Ctrl+A/C/V/X
 * @param {KeyboardEvent} event
 */
export const restrictToNumericKeys = (event) => {
  // Allow: backspace, delete, tab, escape, enter
  if ([8, 9, 27, 13, 46].includes(event.keyCode)) {
    return;
  }

  // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X (and Cmd on Mac)
  if ((event.ctrlKey || event.metaKey) && [65, 67, 86, 88].includes(event.keyCode)) {
    return;
  }

  // Allow: home, end, left, right, up, down arrows
  if (event.keyCode >= 35 && event.keyCode <= 40) {
    return;
  }

  // Prevent: shift + number (symbols), non-numeric keys
  if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) &&
      (event.keyCode < 96 || event.keyCode > 105)) {
    event.preventDefault();
  }
};

/**
 * Handle paste event to allow only numeric content
 * @param {ClipboardEvent} event
 */
export const restrictPasteToNumeric = (event) => {
  const pastedText = event.clipboardData.getData('text');

  // Check if pasted text contains only digits
  if (!/^\d+$/.test(pastedText.trim())) {
    event.preventDefault();
  }
};

/**
 * Format error message for RTL languages
 * @param {string} error - Error message
 * @param {boolean} isRTL - Is right-to-left language
 * @returns {string}
 */
export const formatErrorMessage = (error, isRTL = false) => {
  if (!isRTL) return error;

  // Arabic translations for common errors
  const translations = {
    "must contain only numbers": "يجب أن يحتوي على أرقام فقط",
    "is required": "مطلوب",
    "is not a valid number": "ليس رقماً صحيحاً",
    "is too large": "كبير جداً",
    "must be at least": "يجب أن يكون على الأقل",
    "cannot exceed": "لا يمكن أن يتجاوز",
    "From Year must be less than To Year": "يجب أن تكون 'من السنة' أقل من 'إلى السنة'",
  };

  // Try to translate
  for (const [eng, ar] of Object.entries(translations)) {
    if (error.includes(eng)) {
      return error.replace(eng, ar);
    }
  }

  return error;
};
